import { ToolCall } from "@cohesive/api-client";

/** The AskUserQuestion tool name as it crosses the wire from the Claude harness
 *  (`function.name`). No Codex/OpenCode equivalent exists — Codex declines
 *  elicitations — so this only ever fires for claude-code. */
export const ASK_USER_QUESTION_TOOL = "AskUserQuestion";

export interface AskOption {
  label: string;
  description?: string;
}

export interface AskQuestion {
  question: string;
  /** Short chip label, ≤12 chars per the contract. */
  header: string;
  multiSelect: boolean;
  options: AskOption[];
}

export function isAskUserQuestion(call: ToolCall): boolean {
  return call.function.name === ASK_USER_QUESTION_TOOL;
}

/**
 * Parse an AskUserQuestion call's `{ questions: [...] }` input. `arguments` can
 * arrive as an object or, on some paths, a JSON string. Returns the validated
 * questions, or null for anything malformed — the caller falls back to the
 * generic confirmation card rather than rendering a broken panel.
 */
export function parseAskUserQuestion(call: ToolCall): AskQuestion[] | null {
  let args: unknown = call.function.arguments;
  if (typeof args === "string") {
    try {
      args = JSON.parse(args);
    } catch {
      return null;
    }
  }
  if (!args || typeof args !== "object") return null;
  const raw = (args as { questions?: unknown }).questions;
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > 4) return null;

  const questions: AskQuestion[] = [];
  for (const q of raw) {
    if (!q || typeof q !== "object") return null;
    const { question, header, multiSelect, options } = q as Record<
      string,
      unknown
    >;
    if (typeof question !== "string" || !question.trim()) return null;
    if (!Array.isArray(options) || options.length === 0) return null;
    const parsedOptions: AskOption[] = [];
    for (const o of options) {
      if (!o || typeof o !== "object") return null;
      const label = (o as Record<string, unknown>).label;
      const description = (o as Record<string, unknown>).description;
      if (typeof label !== "string" || !label.trim()) return null;
      parsedOptions.push({
        label,
        description: typeof description === "string" ? description : undefined,
      });
    }
    questions.push({
      question,
      header: typeof header === "string" && header.trim() ? header : question,
      multiSelect: multiSelect === true,
      options: parsedOptions,
    });
  }
  return questions;
}

// ── Stepper state machine ──────────────────────────────────────────────────
//
// Pure and reducer-shaped so the flow (advance / back / Other / multi-select /
// review / auto-submit) is testable without the DOM.

/** One question's answer: chosen option labels, or free "Other" text. */
export type AskSelection =
  | { kind: "options"; labels: string[] }
  | { kind: "other"; text: string };

export type AskPhase = "asking" | "review" | "done";

export interface AskStepperState {
  questions: AskQuestion[];
  /** Index of the question being answered (while phase === "asking"). */
  current: number;
  selections: (AskSelection | undefined)[];
  /** The current question is showing its "Other" free-text field. */
  otherOpen: boolean;
  phase: AskPhase;
}

export type AskStepperAction =
  | { type: "toggleOption"; label: string }
  | { type: "openOther" }
  | { type: "submitOther"; text: string }
  | { type: "next" } // confirm a multi-select / advance
  | { type: "back" }
  | { type: "goto"; index: number }
  | { type: "confirm" }; // from the review step

export function initAskStepper(questions: AskQuestion[]): AskStepperState {
  return {
    questions,
    current: 0,
    selections: questions.map(() => undefined),
    otherOpen: false,
    phase: questions.length ? "asking" : "done",
  };
}

/** Whether every question has an answer — the gate for review/submit. */
export function allAnswered(state: AskStepperState): boolean {
  return state.selections.every(
    (s) =>
      s && (s.kind === "other" ? s.text.trim() !== "" : s.labels.length > 0),
  );
}

// A review step is only worth it when a choice needed thought beyond one tap:
// a multi-select, or a free-text Other answer. All-single-select flows submit
// on the last tap (Claude Code's behaviour).
function needsReview(state: AskStepperState): boolean {
  return state.questions.some(
    (q, i) => q.multiSelect || state.selections[i]?.kind === "other",
  );
}

/** After answering `current`, move to the next unanswered step, or to review /
 *  done once the last question is answered. */
// Whether the Other free-text field should be open for a question — true when
// that question's answer is already an Other one, so revisiting shows it.
function otherOpenAt(state: AskStepperState, index: number): boolean {
  return state.selections[index]?.kind === "other";
}

function advance(state: AskStepperState): AskStepperState {
  const next = state.current + 1;
  if (next < state.questions.length) {
    return { ...state, current: next, otherOpen: otherOpenAt(state, next) };
  }
  if (allAnswered(state)) {
    return {
      ...state,
      otherOpen: false,
      phase: needsReview(state) ? "review" : "done",
    };
  }
  // Shouldn't happen (advance is only called after setting an answer), but keep
  // the machine on the last question rather than overrunning.
  return { ...state, current: state.questions.length - 1, otherOpen: false };
}

export function askStepperReducer(
  state: AskStepperState,
  action: AskStepperAction,
): AskStepperState {
  const q = state.questions[state.current];
  switch (action.type) {
    case "toggleOption": {
      if (!q) return state;
      const prev = state.selections[state.current];
      if (q.multiSelect) {
        const labels = prev?.kind === "options" ? [...prev.labels] : [];
        const at = labels.indexOf(action.label);
        if (at >= 0) labels.splice(at, 1);
        else labels.push(action.label);
        const selections = [...state.selections];
        selections[state.current] = { kind: "options", labels };
        // Multi-select never auto-advances — the user confirms with Next.
        return { ...state, selections, otherOpen: false };
      }
      const selections = [...state.selections];
      selections[state.current] = { kind: "options", labels: [action.label] };
      // Single-select advances on choice.
      return advance({ ...state, selections });
    }
    case "openOther":
      return { ...state, otherOpen: true };
    case "submitOther": {
      if (!action.text.trim()) return state;
      const selections = [...state.selections];
      selections[state.current] = { kind: "other", text: action.text.trim() };
      return advance({ ...state, selections });
    }
    case "next": {
      // Confirm the current answer (multi-select) and advance. No-op if the
      // current question is unanswered.
      const sel = state.selections[state.current];
      if (!sel || (sel.kind === "options" && sel.labels.length === 0)) {
        return state;
      }
      return advance(state);
    }
    case "back": {
      // A fresh, uncommitted Other field collapses back to the options. A
      // committed Other answer's field is the answer itself, so Back leaves the
      // question like any other selection would.
      if (
        state.otherOpen &&
        state.selections[state.current]?.kind !== "other"
      ) {
        return { ...state, otherOpen: false };
      }
      if (state.phase === "review") {
        const last = state.questions.length - 1;
        return {
          ...state,
          phase: "asking",
          current: last,
          otherOpen: otherOpenAt(state, last),
        };
      }
      if (state.current === 0) return state; // caller dismisses into the escape hatch
      const prev = state.current - 1;
      return { ...state, current: prev, otherOpen: otherOpenAt(state, prev) };
    }
    case "goto":
      if (action.index < 0 || action.index >= state.questions.length)
        return state;
      return {
        ...state,
        current: action.index,
        otherOpen: otherOpenAt(state, action.index),
        phase: "asking",
      };
    case "confirm":
      return allAnswered(state) ? { ...state, phase: "done" } : state;
    default:
      return state;
  }
}

// ── Answer formatting ───────────────────────────────────────────────────────

export interface AskAnswer {
  header: string;
  question: string;
  /** The rendered answer value(s): option labels, or the user's Other text. */
  values: string[];
}

export function collectAnswers(state: AskStepperState): AskAnswer[] {
  return state.questions.map((q, i) => {
    const sel = state.selections[i];
    const values =
      sel?.kind === "other"
        ? [sel.text]
        : sel?.kind === "options"
          ? sel.labels
          : [];
    return { header: q.header, question: q.question, values };
  });
}

/**
 * Compose the answers into a plain user message — one `Header: value` line per
 * question, multi-select values joined with ", ", Other text kept verbatim.
 * Written for two readers: the model (which maps each header back to the
 * question it asked) and the human scanning the transcript.
 */
export function formatAskAnswers(answers: AskAnswer[]): string {
  return answers
    .filter((a) => a.values.length > 0)
    .map((a) => `${a.header}: ${a.values.join(", ")}`)
    .join("\n");
}

/**
 * Round-trip the answer message back into a header→value map, for rendering the
 * answered ask as a completed rail step after reload (when only the transcript
 * remains). Tolerant: unmatched lines are ignored.
 */
export function parseAskAnswerMessage(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const at = line.indexOf(":");
    if (at <= 0) continue;
    const header = line.slice(0, at).trim();
    const value = line.slice(at + 1).trim();
    if (header && value) out[header] = value;
  }
  return out;
}
